import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateRiskAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type UpdateRiskAssessmentResultInput = {
  Id: string;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Impacts?: { Label: string; Value: number }[] | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  AssessmentId?: string | null;
  OriginalTimestamp: string;
  ParentRiskId?: string | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (): UpdateRiskAssessmentResultMutation => ({
  updateChildRiskAssessmentResult: {
    __typename: 'GenericMutationOutput',
    affected_rows: 1,
  },
});

export const useUpdateRiskAssessmentResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.riskAssessmentResult.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.resultsRegister.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.riskAssessmentResultsByRiskId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.scores.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskScoresByRiskId.queryKey(),
        });
        // Fast-path hint only: the risk_score upsert runs in an async event
        // consumer, so this races it — the 5s poll in
        // useGetLatestRiskScoresByRiskId is the convergence backstop.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.latestRiskScoresByRiskId.queryKey(),
        });
      },
    }),
  });

  return {
    updateRiskAssessmentResult: async (
      variables: UpdateRiskAssessmentResultInput
    ): Promise<UpdateRiskAssessmentResultMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
